import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import Chart from "react-apexcharts";
import { Dialog } from "primereact/dialog";
import { CustomTable } from "../../componentes/table/CustomTable";
import FilialService from "../../services/resource/filialService";

const filialService = new FilialService();

const formatarMoeda = (valor) => (valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatarMesLabel = (mes) => dayjs(mes + "-01").format("MMM/YYYY");

const SERIES_INFO = [
    { serie: "EMITIDO", titulo: "Valor Emitido (Fortes)" },
    { serie: "COMPENSADO_DATA_COMPENSACAO", titulo: "Valor Compensado (Data Compensação)" },
    { serie: "COMPENSADO_DATA_DOCUMENTO", titulo: "Valor Compensado (Data Documento)" },
];

const colunasFortes = [
    { key: "tipo", label: "Tipo" },
    { key: "numeroCte", label: "Nº Documento" },
    { key: "numeroSerie", label: "Série", render: (v) => v ?? "—" },
    { key: "codigoFilial", label: "Filial" },
    { key: "dataEmissao", label: "Dt. Emissão", format: "date" },
    { key: "valorTotal", label: "Valor", format: "currency" },
    { key: "remetente", label: "Remetente" },
    { key: "chaveEletronica", label: "Chave Eletrônica" },
];

const colunasPagamentos = [
    { key: "numeroDocumento", label: "Nº Documento" },
    { key: "referencia", label: "Referência" },
    { key: "numeroCte", label: "CTE" },
    { key: "numeroSerie", label: "Série", render: (v) => v ?? "—" },
    { key: "filialNome", label: "Filial" },
    { key: "valor", label: "Valor", format: "currency" },
    { key: "dataDocumento", label: "Dt. Documento", format: "date" },
    { key: "dataPagamento", label: "Dt. Pagamento", format: "date" },
    { key: "dataCompensacao", label: "Dt. Compensação", format: "date" },
    { key: "codigoFornecedor", label: "Fornecedor" },
];

function PagamentoCompensadaDashboardPanel({ service }) {
    const [dataInicial, setDataInicial] = useState(dayjs().subtract(5, "month").startOf("month").format("YYYY-MM-DD"));
    const [dataFinal, setDataFinal] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
    const [filialId, setFilialId] = useState("");

    const [filiais, setFiliais] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(false);

    const [detalhe, setDetalhe] = useState(null);
    const [loadingDetalhe, setLoadingDetalhe] = useState(false);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data || []));
    }, []);

    const buscar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await service.confrontoDashboard({ dataInicial, dataFinal, filialId });
            setDashboard(res.data);
        } finally {
            setLoading(false);
        }
    }, [service, dataInicial, dataFinal, filialId]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const serie = dashboard?.serieMensal || [];

    const abrirDetalhe = async (seriesIndex, dataPointIndex) => {
        const ponto = serie[dataPointIndex];
        const info = SERIES_INFO[seriesIndex];
        if (!ponto || !info) return;

        setLoadingDetalhe(true);
        setDetalhe({ mes: ponto.mes, titulo: info.titulo, serieTipo: info.serie, dados: null });
        try {
            const res = await service.confrontoDashboardDetalhe({
                dataInicial, dataFinal, filialId, mes: ponto.mes, serie: info.serie,
            });
            setDetalhe({ mes: ponto.mes, titulo: info.titulo, serieTipo: info.serie, dados: res.data });
        } finally {
            setLoadingDetalhe(false);
        }
    };

    const chartOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    abrirDetalhe(config.seriesIndex, config.dataPointIndex);
                },
            },
        },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "65%" } },
        colors: ["#4f46e5", "#0d9488", "#f59e0b"],
        xaxis: { categories: serie.map(s => formatarMesLabel(s.mes)) },
        yaxis: { labels: { formatter: (v) => formatarMoeda(v) } },
        dataLabels: { enabled: false },
        legend: { position: "top" },
        tooltip: { y: { formatter: (v) => formatarMoeda(v) } },
    };

    const chartSeries = [
        { name: "Valor Emitido (Fortes)", data: serie.map(s => s.valorEmitido) },
        { name: "Valor Compensado (Data Compensação)", data: serie.map(s => s.valorCompensado) },
        { name: "Valor Compensado (Data Documento)", data: serie.map(s => s.valorPagamentoDocumento) },
    ];

    return (
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
            <p className="text-sm font-semibold text-foreground mb-1">Valor Emitido × Valor Compensado</p>
            <p className="text-xs text-muted-foreground mb-4">
                Confronta o valor total dos CTe/NFS-e válidos (não cancelados) emitidos no Fortes com o valor
                efetivamente compensado segundo o SAP, mês a mês — o valor compensado é mostrado tanto pela
                Data de Compensação quanto pela Data do Documento, para os mesmos pagamentos. Clique numa barra
                para ver os registros daquele mês.
            </p>

            <div className="flex flex-wrap gap-3 items-end mb-5">
                <Campo label="Data inicial">
                    <input type="date" value={dataInicial} onChange={e => setDataInicial(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground" />
                </Campo>
                <Campo label="Data final">
                    <input type="date" value={dataFinal} onChange={e => setDataFinal(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground" />
                </Campo>
                <Campo label="Filial">
                    <select value={filialId} onChange={e => setFilialId(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-40">
                        <option value="">Todas</option>
                        {filiais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                </Campo>
            </div>

            {dashboard && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Kpi label="Valor Emitido" valor={formatarMoeda(dashboard.valorEmitidoTotal)} />
                    <Kpi label="Valor Compensado" valor={formatarMoeda(dashboard.valorCompensadoTotal)} />
                    <Kpi
                        label="Diferença (Emitido - Compensado)"
                        valor={formatarMoeda(dashboard.diferencaTotal)}
                        destaque={dashboard.diferencaTotal > 0}
                    />
                    <Kpi
                        label="% Compensado"
                        valor={dashboard.percentualCompensado != null ? `${dashboard.percentualCompensado}%` : "—"}
                    />
                </div>
            )}

            {loading && <p className="text-sm text-muted-foreground">Carregando...</p>}

            {!loading && serie.length > 0 && (
                <Chart options={chartOptions} series={chartSeries} type="bar" height={320} />
            )}

            {!loading && serie.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhum dado encontrado para o período selecionado.</p>
            )}

            <Dialog
                header={detalhe ? `${detalhe.titulo} — ${formatarMesLabel(detalhe.mes)}` : "Detalhes"}
                visible={!!detalhe}
                onHide={() => setDetalhe(null)}
                blockScroll
                className="w-[95%] md:w-[90%]"
            >
                {detalhe?.serieTipo === "EMITIDO" ? (
                    <CustomTable
                        data={detalhe?.dados?.documentosFortes || []}
                        columns={colunasFortes}
                        loading={loadingDetalhe}
                        pagination
                        pageSize={20}
                    />
                ) : (
                    <CustomTable
                        data={detalhe?.dados?.pagamentos || []}
                        columns={colunasPagamentos}
                        loading={loadingDetalhe}
                        pagination
                        pageSize={20}
                    />
                )}
            </Dialog>
        </div>
    );
}

function Campo({ label, children }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">{label}</label>
            {children}
        </div>
    );
}

function Kpi({ label, valor, destaque }) {
    return (
        <div className={`rounded-lg border p-3 ${destaque ? "border-destructive/40 bg-destructive/10" : "border-border bg-muted/40"}`}>
            <p className="text-lg font-bold text-foreground">{valor ?? 0}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
        </div>
    );
}

export default PagamentoCompensadaDashboardPanel;
